import { useState, useEffect, useCallback } from "react";
import { Star, Send, Check, ImagePlus, X, BadgeCheck, ArrowRight, Languages } from "lucide-react";
import ReviewsMarquee from "@/components/ReviewsMarquee";
import ImageLightbox from "@/components/ImageLightbox";

const COPY = {
  ru: {
    badge: "Отзывы покупателей",
    title: "Оставьте",
    titleAccent: "свой отзыв",
    subtitle: "Расскажите о вашем опыте — это помогает другим покупателям и нашим флористам.",
    empty: "Пока нет отзывов для этого города. Станьте первым, кто оставит отзыв!",
    formTitle: "Написать отзыв",
    name: "Ваше имя",
    ratingLabel: "Ваша оценка",
    text: "Ваш отзыв: что понравилось, как прошла доставка, какой букет заказывали",
    photo: "Добавить фото букета",
    submit: "Отправить отзыв",
    sending: "Отправляем…",
    required: "Укажите имя и текст отзыва (минимум 10 символов)",
    successTitle: "Спасибо за отзыв!",
    successText: "Он появится на сайте после проверки модератором. Обычно это занимает несколько часов.",
    errorText: "Не удалось отправить отзыв. Попробуйте ещё раз чуть позже.",
    based: (n) => `${n} ${plural(n, "отзыв", "отзыва", "отзывов")}`,
    verified: "Проверенный отзыв",
  },
  en: {
    badge: "Customer reviews",
    title: "Leave",
    titleAccent: "your review",
    subtitle: "Share your experience — it helps other customers and our florists.",
    empty: "No reviews for this city yet. Be the first to leave one!",
    formTitle: "Write a review",
    name: "Your name",
    ratingLabel: "Your rating",
    text: "Your review: what you liked, how delivery went, which bouquet you ordered",
    photo: "Add a photo of the bouquet",
    submit: "Submit review",
    sending: "Sending…",
    required: "Please add your name and review text (min. 10 characters)",
    successTitle: "Thank you for your review!",
    successText: "It will appear on the site after moderator approval — usually within a few hours.",
    errorText: "Could not submit the review. Please try again a little later.",
    based: (n) => `${n} review${n === 1 ? "" : "s"}`,
    verified: "Verified review",
  },
};

const LANG_META = {
  ru: { flag: "🇷🇺", label: "Русский", labelEn: "Russian" },
  en: { flag: "🇬🇧", label: "Английский", labelEn: "English" },
  tr: { flag: "🇹🇷", label: "Турецкий", labelEn: "Turkish" },
  de: { flag: "🇩🇪", label: "Немецкий", labelEn: "German" },
  fr: { flag: "🇫🇷", label: "Французский", labelEn: "French" },
  es: { flag: "🇪🇸", label: "Испанский", labelEn: "Spanish" },
  it: { flag: "🇮🇹", label: "Итальянский", labelEn: "Italian" },
  ar: { flag: "🇸🇦", label: "Арабский", labelEn: "Arabic" },
  th: { flag: "🇹🇭", label: "Тайский", labelEn: "Thai" },
};

function plural(n, one, few, many) {
  const m10 = n % 10, m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return one;
  if (m10 >= 2 && m10 <= 4 && (m100 < 10 || m100 >= 20)) return few;
  return many;
}

const Stars = ({ value, size = "w-4 h-4" }) => (
  <div className="flex gap-0.5" aria-hidden="true">
    {[1, 2, 3, 4, 5].map((i) => (
      <Star key={i} className={`${size} ${i <= value ? "fill-amber-400 text-amber-400" : "fill-gray-200 text-gray-200"}`} />
    ))}
  </div>
);

const ReviewItem = ({ r, lang, verifiedLabel, onPhotoClick }) => {
  const [showOriginal, setShowOriginal] = useState(false);
  const date = r.ts ? new Date(r.ts).toLocaleDateString(lang === "en" ? "en-GB" : "ru-RU", { day: "2-digit", month: "long", year: "numeric" }) : "";
  const initial = (r.name || "?").trim().charAt(0).toUpperCase();
  const origLang = (r.lang || "ru").slice(0, 2).toLowerCase();
  const translated = origLang !== lang ? (r.translations && r.translations[lang]) : "";
  const hasTranslation = !!translated;
  const displayText = hasTranslation && !showOriginal ? translated : r.text;
  const meta = LANG_META[origLang];
  const langLabel = meta ? (lang === "en" ? meta.labelEn : meta.label) : origLang.toUpperCase();
  const T = {
    ru: { translated: "Переведено", showOriginal: "Показать оригинал", showTranslation: "Показать перевод" },
    en: { translated: "Translated", showOriginal: "Show original", showTranslation: "Show translation" },
  }[lang === "en" ? "en" : "ru"];
  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 sm:p-6 flex flex-col" data-testid={`review-item-${r.id}`}>
      <div className="flex items-center gap-3 mb-3">
        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-pink-500 to-rose-500 text-white flex items-center justify-center font-bold shrink-0">
          {initial}
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-2 min-w-0">
            <span className="font-semibold text-gray-900 truncate">{r.name}</span>
            {meta && (
              <span
                className="inline-flex items-center gap-1 shrink-0 px-1.5 py-0.5 rounded-md bg-gray-50 border border-gray-100 text-[10px] font-medium text-gray-500"
                title={langLabel}
                data-testid={`review-lang-badge-${r.id}`}
              >
                <span aria-hidden="true">{meta.flag}</span>
                <span className="uppercase tracking-wide">{origLang}</span>
              </span>
            )}
          </div>
          <div className="flex items-center gap-1 text-[11px] text-emerald-600 font-medium">
            <BadgeCheck className="w-3.5 h-3.5" /> {verifiedLabel}
          </div>
        </div>
        <div className="ml-auto text-right">
          <Stars value={r.rating} />
          {date && <div className="text-[11px] text-gray-400 mt-1">{date}</div>}
        </div>
      </div>
      <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap flex-1" data-testid={`review-text-${r.id}`}>{displayText}</p>
      {hasTranslation && (
        <div className="mt-2 flex items-center gap-2 text-[11px] text-gray-400">
          {!showOriginal && <span className="inline-flex items-center gap-1"><Languages className="w-3 h-3" /> {T.translated} · {lang === "en" ? `from ${langLabel}` : `с языка: ${langLabel}`}</span>}
          <button
            type="button"
            onClick={() => setShowOriginal((v) => !v)}
            className="text-pink-500 hover:text-pink-600 font-medium underline underline-offset-2 transition-colors"
            data-testid={`review-toggle-original-${r.id}`}
          >
            {showOriginal ? T.showTranslation : T.showOriginal}
          </button>
        </div>
      )}
      {r.photo_url && (
        <img
          src={r.photo_url}
          alt={`${r.name} — ${lang === "en" ? "customer photo" : "фото клиента"}`}
          className="mt-4 rounded-xl w-full max-h-64 object-cover cursor-zoom-in hover:opacity-95 transition"
          loading="lazy"
          onClick={() => onPhotoClick && onPhotoClick({ src: r.photo_url, alt: `${r.name} — ${lang === "en" ? "customer photo" : "фото клиента"}` })}
          data-testid={`review-photo-${r.id}`}
        />
      )}
    </div>
  );
};

export default function CityReviews({ city = "", lang = "ru", formFirst = false, hideHeader = false, allReviewsHref = null }) {
  const c = COPY[lang] || COPY.en;
  const [reviews, setReviews] = useState([]);
  const [avg, setAvg] = useState(0);
  const [count, setCount] = useState(0);
  const [form, setForm] = useState({ name: "", text: "" });
  const [rating, setRating] = useState(5);
  const [hoverRating, setHoverRating] = useState(0);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState("");
  const [hp, setHp] = useState("");
  const [state, setState] = useState("idle"); // idle | sending | success | error
  const [err, setErr] = useState("");
  const [lightbox, setLightbox] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await fetch(`/api/reviews?city=${encodeURIComponent(city)}&limit=30&lang=${lang}`);
      const d = await r.json();
      setReviews(d.reviews || []);
      setAvg(d.average || 0);
      setCount(d.count || 0);
    } catch { /* keep empty */ }
  }, [city, lang]);

  useEffect(() => { load(); }, [load]);

  const onPhoto = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setPhotoFile(f);
    setPhotoPreview(URL.createObjectURL(f));
  };

  const clearPhoto = () => {
    setPhotoFile(null);
    setPhotoPreview("");
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (form.name.trim().length < 2 || form.text.trim().length < 10) {
      setErr(c.required);
      return;
    }
    setState("sending");
    try {
      const fd = new FormData();
      fd.append("name", form.name.trim());
      fd.append("city", city);
      fd.append("rating", String(rating));
      fd.append("text", form.text.trim());
      fd.append("lang", lang);
      fd.append("hp", hp);
      if (photoFile) fd.append("photo", photoFile);
      const r = await fetch(`/api/reviews`, { method: "POST", body: fd });
      if (!r.ok) throw new Error("bad");
      setState("success");
    } catch {
      setState("error");
    }
  };

  const displayRating = hoverRating || rating;

  return (
    <>
    <section id="reviews" className="py-16 sm:py-24 bg-white scroll-mt-20" data-testid="city-reviews-section">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        {!hideHeader && (
        <div className="text-center mb-10 sm:mb-14">
          <span className="inline-block bg-gradient-to-r from-pink-500 to-rose-500 text-white text-xs sm:text-sm font-semibold px-4 py-1.5 rounded-full mb-4">
            {c.badge}
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4" style={{ fontFamily: "Playfair Display, serif" }}>
            {c.title} <span className="text-transparent bg-clip-text bg-gradient-to-r from-pink-500 to-rose-500">{c.titleAccent}</span>
          </h2>
          {count > 0 && (
            <div className="flex items-center justify-center gap-3 mt-3" data-testid="reviews-aggregate">
              <Stars value={Math.round(avg)} size="w-5 h-5" />
              <span className="text-lg font-bold text-gray-900">{avg.toFixed(1)}</span>
              <span className="text-gray-500 text-sm">· {c.based(count)}</span>
            </div>
          )}
          <p className="text-gray-500 max-w-xl mx-auto text-base sm:text-lg mt-3">{c.subtitle}</p>
          {allReviewsHref && (
            <a href={allReviewsHref} className="inline-flex items-center gap-1.5 mt-5 px-5 py-2.5 rounded-full border-2 border-pink-200 text-pink-600 text-sm font-semibold hover:bg-pink-50 transition" data-testid="all-reviews-link">
              {lang === "en" ? "All reviews" : "Все отзывы"} <ArrowRight className="w-4 h-4" />
            </a>
          )}
        </div>
        )}

        <div className="flex flex-col">
        {/* Reviews grid */}
        <div style={{ order: formFirst ? 2 : 1 }}>
        {reviews.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mb-12" data-testid="reviews-list">
            {reviews.map((r) => (
              <ReviewItem key={r.id} r={r} lang={lang} verifiedLabel={c.verified} onPhotoClick={setLightbox} />
            ))}
          </div>
        ) : (
          <p className="text-center text-gray-400 mb-12" data-testid="reviews-empty">{c.empty}</p>
        )}
        </div>

        {/* Submission form */}
        <div className="max-w-2xl mx-auto w-full" style={{ order: formFirst ? 1 : 2 }}>
          {state === "success" ? (
            <div className="bg-gradient-to-br from-pink-50 via-white to-rose-50 border border-pink-100 rounded-3xl p-8 sm:p-12 text-center shadow-sm" data-testid="review-success">
              <div className="w-14 h-14 rounded-full bg-gradient-to-r from-pink-500 to-rose-500 text-white flex items-center justify-center mx-auto mb-4 shadow-lg shadow-pink-200">
                <Check className="w-7 h-7" strokeWidth={2.5} />
              </div>
              <h3 className="text-xl sm:text-2xl text-gray-900 mb-3 font-bold" style={{ fontFamily: "Playfair Display, serif" }}>{c.successTitle}</h3>
              <p className="text-sm sm:text-base text-gray-500 leading-relaxed max-w-md mx-auto">{c.successText}</p>
            </div>
          ) : (
            <form onSubmit={submit} className="bg-white border-2 border-gray-100 rounded-3xl p-6 sm:p-8 shadow-xl shadow-pink-100/40 space-y-5" data-testid="review-form">
              <h3 className="text-xl sm:text-2xl font-bold text-gray-900" style={{ fontFamily: "Playfair Display, serif" }}>{c.formTitle}</h3>

              {/* Honeypot (hidden from humans) */}
              <input
                type="text"
                tabIndex={-1}
                autoComplete="off"
                value={hp}
                onChange={(e) => setHp(e.target.value)}
                className="hidden"
                aria-hidden="true"
                data-testid="review-hp"
              />

              <input
                type="text"
                required
                placeholder={c.name}
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full px-4 py-3.5 bg-white border-2 border-gray-100 focus:border-pink-300 outline-none text-gray-900 text-sm placeholder-gray-400 rounded-2xl transition"
                data-testid="review-name"
              />

              {/* Rating stars */}
              <div>
                <div className="text-sm text-gray-500 mb-2">{c.ratingLabel}</div>
                <div className="flex gap-1" onMouseLeave={() => setHoverRating(0)}>
                  {[1, 2, 3, 4, 5].map((i) => (
                    <button
                      type="button"
                      key={i}
                      onClick={() => setRating(i)}
                      onMouseEnter={() => setHoverRating(i)}
                      className="p-0.5 transition-transform hover:scale-110"
                      aria-label={`${i} / 5`}
                      data-testid={`review-rating-${i}`}
                    >
                      <Star className={`w-8 h-8 ${i <= displayRating ? "fill-amber-400 text-amber-400" : "fill-gray-200 text-gray-200"}`} />
                    </button>
                  ))}
                </div>
              </div>

              <textarea
                rows={4}
                required
                placeholder={c.text}
                value={form.text}
                onChange={(e) => setForm({ ...form, text: e.target.value })}
                className="w-full px-4 py-3.5 bg-white border-2 border-gray-100 focus:border-pink-300 outline-none text-gray-900 text-sm placeholder-gray-400 rounded-2xl transition resize-none"
                data-testid="review-text"
              />

              {/* Photo upload */}
              {photoPreview ? (
                <div className="relative inline-block">
                  <img src={photoPreview} alt="preview" className="h-28 w-28 object-cover rounded-2xl border-2 border-pink-100" />
                  <button type="button" onClick={clearPhoto} className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-gray-900 text-white flex items-center justify-center shadow-lg" data-testid="review-photo-clear">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <label className="inline-flex items-center gap-2 px-4 py-2.5 border-2 border-dashed border-gray-200 rounded-2xl text-sm text-gray-500 cursor-pointer hover:border-pink-300 hover:text-pink-500 transition" data-testid="review-photo-label">
                  <ImagePlus className="w-4 h-4" /> {c.photo}
                  <input type="file" accept="image/*" onChange={onPhoto} className="hidden" data-testid="review-photo" />
                </label>
              )}

              {err && <p className="text-rose-500 text-sm" data-testid="review-error">{err}</p>}
              {state === "error" && <p className="text-rose-500 text-sm" data-testid="review-error-server">{c.errorText}</p>}

              <button
                type="submit"
                disabled={state === "sending"}
                className="group inline-flex items-center justify-center gap-2 w-full sm:w-auto px-8 py-4 bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600 text-white text-sm font-semibold rounded-full shadow-lg shadow-pink-200 transition-all hover:scale-[1.02] disabled:opacity-60 disabled:cursor-wait disabled:hover:scale-100"
                data-testid="review-submit"
              >
                <Send className="w-4 h-4" /> {state === "sending" ? c.sending : c.submit}
              </button>
            </form>
          )}
        </div>
        </div>
      </div>
    </section>
    <ReviewsMarquee city={city} lang={lang} />
    <ImageLightbox src={lightbox?.src} alt={lightbox?.alt} onClose={() => setLightbox(null)} />
    </>
  );
}
